import React, { useMemo, useCallback } from "react"
import { toast } from "react-toastify"

import { ConnectorDialog } from "~/ui-rtk/components/features/connectors"
import { useAirbyteAdditionalConfig } from "~/ui-rtk/hooks/useAirbyteAdditionalConfig"
import { Button } from "~/ui-rtk/components/ui/controls"
import { ConnectorDto } from "~/ui-rtk/api/types"
import { confirmationDialogPropsMap } from "~/ui-rtk/components/features/connectors/ConnectorsTable/utils"

import { useConfigFormState } from "./hooks/useConfigFormState"
import { ConfigFormField } from "./components"
import type { AdditionalConfigField } from "./types"

interface AdditionalConfigFormProps {
  companyConnectorId: string
  connector: ConnectorDto
  isOpen: boolean
  onClose: () => void
  configPhase?: "oauth" | "source"
  onSubmit?: (values: Record<string, unknown>) => Promise<void>
}

/**
 * Flexible additional configuration form for connectors
 * Supports multiple connector types with different configuration requirements
 */
const AdditionalConfigForm: React.FC<AdditionalConfigFormProps> = ({
  companyConnectorId,
  connector,
  isOpen,
  onClose,
  configPhase,
  onSubmit,
}) => {
  const {
    completeSetup,
    isCompletingSetup,
    isLoadingProperties,
    getAdditionalConfigSchema,
    getPreConfigSchema,
    getSourceConfigSchema,
    getMetadata,
  } = useAirbyteAdditionalConfig(companyConnectorId, connector)

  // Get the additional configuration schema for this connector
  const additionalConfigSchema = useMemo(() => {
    if (configPhase === "oauth") {
      return getPreConfigSchema()
    } else if (configPhase === "source") {
      return getSourceConfigSchema()
    }
    return getAdditionalConfigSchema()
  }, [
    configPhase,
    getAdditionalConfigSchema,
    getPreConfigSchema,
    getSourceConfigSchema,
  ])

  // Get existing metadata
  const existingMetadata = useMemo(() => getMetadata(), [getMetadata])

  // Use form state hook
  const {
    formData,
    selectedPrefixes,
    tagInputValues,
    errorMessage,
    handleInputChange,
    handlePrefixChange,
    handleTagInputChange,
    addTag,
    removeTag,
    getTags,
    setErrorMessage,
  } = useConfigFormState({
    additionalConfigSchema: additionalConfigSchema as AdditionalConfigField[],
    existingMetadata,
  })

  // Check if form is valid (all required fields filled)
  const isFormValid = useMemo(() => {
    if (additionalConfigSchema.length === 0) return true

    return (additionalConfigSchema as AdditionalConfigField[]).every(config => {
      const fieldName = config.configuration_path.split(".").pop()
      if (!fieldName) return false

      const value = formData[fieldName]
      const isMainFieldFilled =
        value !== undefined &&
        value !== null &&
        value !== "" &&
        (!Array.isArray(value) || value.length > 0)

      // Enforce maxItems constraint
      if (
        isMainFieldFilled &&
        config.maxItems !== undefined &&
        Array.isArray(value) &&
        value.length > config.maxItems
      ) {
        return false
      }

      if (config.property_type_field) {
        const propertyTypeValue = formData[config.property_type_field]
        const isPropertyTypeFilled =
          propertyTypeValue !== undefined &&
          propertyTypeValue !== null &&
          propertyTypeValue !== ""
        return isMainFieldFilled && isPropertyTypeFilled
      }

      return isMainFieldFilled
    })
  }, [additionalConfigSchema, formData])

  // Helper to sanitize values (e.g., strip dashes from IDs like Google Ads Customer ID)
  // Note: For YouTube channel names, we preserve hyphens as they're valid in handles
  const sanitizeValue = (value: string, fieldName?: string): string => {
    // Don't strip hyphens from the YouTube field: they are valid in handles and in
    // channel IDs (each of the 22 trailing base64url characters can be "-").
    // The exemption used to name channel_name, which no config declares any more --
    // the live field is channel_ids -- so every value was silently losing them.
    if (fieldName === "channel_ids" || fieldName === "shop") {
      // Only remove spaces, keep hyphens
      return value.replace(/\s/g, "")
    }
    // Remove dashes and spaces that users might copy from formatted IDs
    return value.replace(/[-\s]/g, "")
  }

  // Validate pattern constraints for comma-separated list formats
  // This runs on processed data AFTER sanitization
  const validatePatterns = useCallback(
    (processedData: Record<string, unknown>): AdditionalConfigField | null => {
      for (const config of additionalConfigSchema as AdditionalConfigField[]) {
        // Skip validation if no pattern, or for list_of_numbers (backend handles number validation)
        if (
          !config.pattern ||
          !config.format.startsWith("list_of_") ||
          config.format === "list_of_numbers"
        ) {
          continue
        }

        const fieldName = config.configuration_path.split(".").pop()
        if (!fieldName) continue

        const value = processedData[fieldName]
        if (!value || (typeof value !== "string" && !Array.isArray(value))) {
          continue
        }

        // Get the string representation to test (already sanitized at this point)
        const valueToTest = Array.isArray(value)
          ? value.join(",")
          : String(value)

        // Test against pattern
        const regex = new RegExp(config.pattern)
        if (!regex.test(valueToTest)) {
          return config // Return the invalid field config
        }
      }
      return null // All valid
    },
    [additionalConfigSchema],
  )

  // Process form data before submission
  const processFormData = useCallback(
    (data: Record<string, unknown>): Record<string, unknown> => {
      const processedData = { ...data }

      ;(additionalConfigSchema as AdditionalConfigField[]).forEach(config => {
        const fieldName = config.configuration_path.split(".").pop()
        if (!fieldName) return

        let value = processedData[fieldName]

        // Sanitize string values that have a pattern (like Customer IDs)
        // This allows users to paste formatted IDs with dashes
        // Note: channel_ids preserves hyphens as they're valid in handles and IDs
        if (config.pattern && typeof value === "string") {
          value = sanitizeValue(value, fieldName)
          processedData[fieldName] = value
        }

        // Determine prefix to apply
        const propertyType = config.property_type_field
          ? formData[config.property_type_field]
          : null
        const selectedOption =
          propertyType && config.property_type_options
            ? config.property_type_options[propertyType as string]
            : null

        let prefixToApply = ""
        if (selectedOption) {
          if (selectedOption.prefix_options?.length) {
            prefixToApply = selectedPrefixes[fieldName] || ""
          } else if (selectedOption.prefix) {
            prefixToApply = selectedOption.prefix
          }
        }

        // Process based on format
        if (config.format === "list" && Array.isArray(value)) {
          // Sanitize each item if there's a pattern
          let items = config.pattern
            ? value.map((item: string) => sanitizeValue(item, fieldName))
            : value
          if (prefixToApply) {
            items = items.map((item: string) =>
              item.startsWith(prefixToApply) ? item : `${prefixToApply}${item}`,
            )
          }
          processedData[fieldName] = items
        } else if (config.format === "list" && typeof value === "string") {
          let items = value
            .split(",")
            .map((item: string) => item.trim())
            .filter((item: string) => item.length > 0)

          // Sanitize each item if there's a pattern
          if (config.pattern) {
            items = items.map((item: string) => sanitizeValue(item, fieldName))
          }

          if (prefixToApply) {
            items = items.map((item: string) => `${prefixToApply}${item}`)
          }
          processedData[fieldName] = items
        } else if (
          config.format.startsWith("list_of_") &&
          typeof value === "string"
        ) {
          // Handle comma-separated list formats generically
          if (config.format === "list_of_numbers") {
            // For numbers: sanitize to remove dashes/spaces, then convert to numbers
            const items = value
              .split(",")
              .map((item: string) => sanitizeValue(item.trim(), fieldName))
              .filter((item: string) => item.length > 0)

            const numItems = items.map((item: string) => {
              const num = Number(item)
              if (isNaN(num) || !Number.isFinite(num)) {
                throw new Error(`Invalid number: ${item}`)
              }
              return num
            })
            processedData[fieldName] = numItems
          } else {
            // For other list_of_* formats (e.g., list_of_strings): keep as-is, only trim
            const items = value
              .split(",")
              .map((item: string) => item.trim())
              .filter((item: string) => item.length > 0)
            processedData[fieldName] = items
          }
        } else if (
          config.format === "list" &&
          value &&
          typeof value === "object" &&
          "value" in value
        ) {
          const itemValue = prefixToApply
            ? `${prefixToApply}${value.value}`
            : value.value
          processedData[fieldName] = [itemValue]
        } else if (
          prefixToApply &&
          typeof value === "string" &&
          value.length > 0
        ) {
          processedData[fieldName] = `${prefixToApply}${value}`
        }
      })

      return processedData
    },
    [additionalConfigSchema, formData, selectedPrefixes],
  )

  const handleConnect = async () => {
    try {
      // If no additional config is needed
      if (additionalConfigSchema.length === 0) {
        if (onSubmit) {
          await onSubmit({})
        } else {
          await completeSetup({})
        }
        onClose()
        return
      }

      // Validate required fields
      const missingFields = (
        additionalConfigSchema as AdditionalConfigField[]
      ).filter(config => {
        const fieldName = config.configuration_path.split(".").pop()
        if (!fieldName) return true

        const isMainFieldMissing =
          formData[fieldName] === undefined ||
          formData[fieldName] === null ||
          formData[fieldName] === ""

        if (config.property_type_field) {
          const propertyTypeMissing =
            formData[config.property_type_field] === undefined ||
            formData[config.property_type_field] === null ||
            formData[config.property_type_field] === ""
          return isMainFieldMissing || propertyTypeMissing
        }

        return isMainFieldMissing
      })

      if (missingFields.length > 0) {
        toast.error("Please fill in all required fields", {
          toastId: "missing-required-fields",
        })
        return
      }

      // Validate maxItems constraints
      const overLimitFields = (
        additionalConfigSchema as AdditionalConfigField[]
      ).filter(config => {
        const fieldName = config.configuration_path.split(".").pop()
        if (!fieldName || config.maxItems === undefined) return false
        const value = formData[fieldName]
        return Array.isArray(value) && value.length > config.maxItems
      })

      if (overLimitFields.length > 0) {
        const field = overLimitFields[0]
        toast.error(
          `${field.property_label} cannot have more than ${field.maxItems} ${field.maxItems === 1 ? "item" : "items"}`,
          { toastId: "max-items-exceeded" },
        )
        return
      }

      // Process form data (sanitization happens here)
      const processedData = processFormData(formData)

      // Validate patterns AFTER sanitization
      const invalidField = validatePatterns(processedData)
      if (invalidField) {
        const message =
          invalidField.pattern_message ||
          `Invalid format for ${invalidField.property_label}`
        toast.error(message, {
          toastId: "invalid-field-format",
        })
        return
      }

      setErrorMessage(null)

      if (onSubmit) {
        await onSubmit(processedData)
        onClose()
      } else {
        await completeSetup(processedData)
        onClose()
      }
    } catch (error: unknown) {
      let message = "An error occurred"

      const err = error as Record<string, unknown> | undefined
      const errData = err?.data as Record<string, unknown> | undefined
      if (errData?.message) {
        message = String(errData.message)
        if (Array.isArray(errData.errors) && errData.errors.length > 0) {
          message = errData.errors.join(", ")
        }
      } else if (err?.message) {
        message = String(err.message)
      }

      setErrorMessage(message)
    }
  }

  // Get connector dialog props
  const dialogProps = confirmationDialogPropsMap[connector.id]

  return (
    <ConnectorDialog
      imgSrc={dialogProps?.imgSrc || connector.icon}
      alt={dialogProps?.alt || `${connector.name} Logo`}
      title={dialogProps?.title || `Connection to ${connector.name}`}
      isOpen={isOpen}
      onClose={onClose}
    >
      {isLoadingProperties && (
        <div className="flex flex-col w-full gap-4 mt-8">
          <p className="leading-5 text-left text-base text-platinum">
            Loading configuration...
          </p>
        </div>
      )}
      {!isLoadingProperties && additionalConfigSchema.length === 0 && (
        <div className="flex flex-col w-full gap-4 mt-8">
          <p className="leading-5 text-left text-base text-platinum">
            This connector doesn&apos;t require additional configuration.
          </p>

          <Button
            variant={{ variant: "solid", color: "white" }}
            onClick={handleConnect}
            isLoading={isCompletingSetup}
            disabled={isCompletingSetup}
            className="w-full"
          >
            Complete Setup
          </Button>
        </div>
      )}
      {!isLoadingProperties && additionalConfigSchema.length > 0 && (
        <>
          <p className="leading-5 text-left text-base text-platinum">
            Complete the setup for {connector.name} by providing the required
            configuration.
          </p>

          <div className="flex flex-col w-full gap-4 mt-8">
            <div className="space-y-6">
              {(additionalConfigSchema as AdditionalConfigField[]).map(
                config => (
                  <ConfigFormField
                    key={config.configuration_path}
                    config={config}
                    formData={formData}
                    selectedPrefixes={selectedPrefixes}
                    tagInputValues={tagInputValues}
                    onInputChange={handleInputChange}
                    onPrefixChange={handlePrefixChange}
                    onTagInputChange={handleTagInputChange}
                    onRemoveTag={removeTag}
                    onAddTag={addTag}
                    getTags={getTags}
                  />
                ),
              )}
            </div>

            {errorMessage && (
              <div className="p-3 rounded-lg">
                <p className="text-sm font-medium text-system-error-400">
                  {errorMessage}
                </p>
              </div>
            )}

            <Button
              variant={{ variant: "solid", color: "white" }}
              onClick={handleConnect}
              isLoading={isCompletingSetup}
              disabled={
                isCompletingSetup || isLoadingProperties || !isFormValid
              }
              className="w-full"
            >
              Complete Setup
            </Button>
          </div>
        </>
      )}
    </ConnectorDialog>
  )
}

export default AdditionalConfigForm
